import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Star } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useLocales, useUpdateLocale } from "./use-locales";
import type { SupportedLocale } from "../shared/compliance-types";
import { CreateLocaleDialog } from "./CreateLocaleDialog";
import { EditLocaleDialog } from "./EditLocaleDialog";
import { DeleteLocaleDialog } from "./DeleteLocaleDialog";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

export function LocalesPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const { data: items, isLoading, isError } = useLocales();
  const update = useUpdateLocale();
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SupportedLocale | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SupportedLocale | null>(null);

  const handlePromoteCanonical = async (locale: SupportedLocale) => {
    if (locale.isCanonical || update.isPending) return;
    setPromoteError(null);
    try {
      await update.mutateAsync({ code: locale.code, input: { isCanonical: true } });
    } catch (err) {
      setPromoteError(resolveComplianceError(err, t));
    }
  };

  const headerActions = canEdit ? (
    <Button data-testid="compliance-new-locale-button" onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      {t("compliance.locales.create.button")}
    </Button>
  ) : null;

  return (
    <CompliancePageShell
      screenTestId="compliance-locales-screen"
      titleKey="compliance.locales.title"
      subtitleKey="compliance.locales.subtitle"
      headerActions={headerActions}
    >
      {isLoading && (
        <p data-testid="compliance-locales-loading" className="text-sm text-muted-foreground">
          {t("compliance.locales.loading")}
        </p>
      )}

      {isError && (
        <p data-testid="compliance-locales-error" className="text-sm text-destructive">
          {t("compliance.locales.loadError")}
        </p>
      )}

      {promoteError && (
        <p data-testid="compliance-promote-canonical-error" className="text-sm text-destructive">
          {promoteError}
        </p>
      )}

      {items && items.length === 0 && (
        <p data-testid="compliance-locales-empty" className="text-sm text-muted-foreground">
          {t("compliance.locales.empty")}
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((locale) => (
            <li
              key={locale.code}
              data-testid={`compliance-locale-row-${locale.code}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {locale.code}
                  </code>
                  {locale.isCanonical && (
                    <span
                      data-testid={`compliance-locale-canonical-${locale.code}`}
                      className="inline-flex items-center gap-1 rounded-full bg-wander-orange/15 px-2 py-0.5 text-xs text-wander-orange ring-1 ring-inset ring-wander-orange/40"
                    >
                      <Star className="h-3 w-3" aria-hidden="true" />
                      {t("compliance.locales.defaultBadge")}
                    </span>
                  )}
                </div>
                <p className="font-medium">{locale.displayName}</p>
              </div>

              {canEdit && (
                <div className="flex shrink-0 gap-2">
                  {!locale.isCanonical && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`compliance-promote-canonical-${locale.code}`}
                      onClick={() => handlePromoteCanonical(locale)}
                      disabled={update.isPending}
                    >
                      {t("compliance.locales.actions.promoteDefault")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`compliance-edit-locale-${locale.code}`}
                    onClick={() => setEditTarget(locale)}
                  >
                    {t("compliance.actions.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={locale.isCanonical}
                    title={
                      locale.isCanonical ? t("compliance.locales.defaultDeleteTooltip") : undefined
                    }
                    data-testid={`compliance-delete-locale-${locale.code}`}
                    onClick={() => setDeleteTarget(locale)}
                  >
                    {t("compliance.actions.delete")}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <CreateLocaleDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditLocaleDialog target={editTarget} onOpenChange={(open) => !open && setEditTarget(null)} />
      <DeleteLocaleDialog
        target={deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />
    </CompliancePageShell>
  );
}
